import { FunctionComponent, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "../components/layout";
import { ROUTES } from "constants/routes";
import { useId } from "react-id-generator";
import AuthProivder from "providers/auth";
import GoogleProvider from "providers/google";
import { PermissionAction, PermissionSubject } from "types/permissions";
import ProtectedRoute from "components/protected-route";
import SettingsProvider from "providers/settings";

const DashboardPage = lazy(() => import("./dashboard"));
const DashboardV2Page = lazy(() => import("./dashboard-v2"));
const PlacesPage = lazy(() => import("./places"));
const StoragesPage = lazy(() => import("./storages"));
const AnomaliesPage = lazy(() => import("./anomalies"));
const StatusPage = lazy(() => import("./status/status"));
const ClientsPage = lazy(() => import("./clients"));
const OrdersPage = lazy(() => import("./orders"));
const ServicesPage = lazy(() => import("./services"));
const BillingPage = lazy(() => import("./billing"));
const UsagePage = lazy(() => import("./usage"));
const UsageReportPage = lazy(() => import("./usage-report"));
const UsageSettingsPage = lazy(() => import("./usage-settings"));
const CentralConfigurationPage = lazy(() => import("./central-configuration"));
const MembersPage = lazy(() => import("./members"));
const SettingsPage = lazy(() => import("./settings"));
const MemberPage = lazy(() => import("./member"));
const InvoicePage = lazy(() => import("./invoice"));
const ServicePage = lazy(() => import("./service"));
const ViewStoragePage = lazy(() => import("./storage/view-storage"));
const ClientPage = lazy(() => import("./client/view-client"));
const ClientCreatePage = lazy(() => import("./client/create-client"));
const ClientEditPage = lazy(() => import("./client/edit-client"));
const EditStoragePage = lazy(() => import("./storage/edit-storage"));
const NewStoragePage = lazy(() => import("./storage/create-storage"));
const DriverLicensesPage = lazy(() => import("./driver-licenses"));
const DriverLicensePage = lazy(() => import("./driver-licenses/driver-license"));
const CarRentalCostsPage = lazy(() => import("./car-rental-costs"));
const SupportTicketsPage = lazy(() => import("./support-tickets"));
const ClientRecordsPage = lazy(() => import("./client-records"));
const ContractRecordsPage = lazy(() => import("./contract-records"));
const ClientRecordPage = lazy(() => import("./client-records/client-record"));
const ClientRecordAssignUserPage = lazy(() => import("./client-records/assign-user"));
const ClientRecordCreateUserPage = lazy(() => import("./client-records/create-user"));
const ClientRecordCreateContractPage = lazy(() => import("./client-records/create-contract"));
const ClientRecordEditContractPage = lazy(() => import("./client-records/edit-contract"));
const SupportTicketTypesPage = lazy(() => import("./support-ticket-types"));
const SupportTicketTypePage = lazy(() => import("./support-ticket-types/support-ticket-type"));
const ViewSupportTicketPage = lazy(() => import("./support-tickets/view-support-ticket"));
const EvChargersPage = lazy(() => import("./ev-chargers"));
const EvChargerPage = lazy(() => import("./ev-chargers/ev-charger"));
const EvTransactionsPage = lazy(() => import("./ev-transactions"));
const HeatPumpsPage = lazy(() => import("./heat-pumps"));
const HeatPumpPage = lazy(() => import("./heat-pumps/heat-pump"));

/** Rewrites legacy /clients/... links to the current /users/... path. */
const ClientsRedirect: FunctionComponent = () => {
  const { pathname, search } = useLocation();

  return <Navigate to={`${pathname.replace(/^\/clients/, ROUTES.clients)}${search}`} replace />;
};

interface PagesProps {}

const Pages: FunctionComponent<PagesProps> = () => {
  const pages: {
    element: typeof DashboardPage;
    path: string;
    permission: { action: PermissionAction; subject: PermissionSubject };
  }[] = [
    { element: DashboardV2Page, path: ROUTES.dashboard, permission: { action: "read", subject: "dashboard" } },
    { element: DashboardPage, path: ROUTES.dashboardLegacy, permission: { action: "read", subject: "dashboard" } },
    { element: PlacesPage, path: ROUTES.places, permission: { action: "read", subject: "storages" } },
    { element: StoragesPage, path: ROUTES.storages, permission: { action: "read", subject: "storages" } },
    { element: AnomaliesPage, path: ROUTES.anomalies, permission: { action: "read", subject: "anomalies" } },
    { element: StatusPage, path: ROUTES.status, permission: { action: "read", subject: "storages" } },
    { element: ClientsPage, path: ROUTES.clients, permission: { action: "read", subject: "clients" } },
    { element: OrdersPage, path: ROUTES.orders, permission: { action: "read", subject: "orders" } },
    { element: ServicesPage, path: ROUTES.services, permission: { action: "read", subject: "services" } },
    { element: BillingPage, path: ROUTES.billing, permission: { action: "read", subject: "invoices" } },
    { element: UsagePage, path: ROUTES.usage, permission: { action: "read", subject: "storages" } },
    { element: UsageReportPage, path: ROUTES.usageReport, permission: { action: "read", subject: "storages" } },
    { element: UsageSettingsPage, path: ROUTES.usageSettings, permission: { action: "read", subject: "settings" } },
    { element: CentralConfigurationPage, path: ROUTES.centralConfiguration, permission: { action: "read", subject: "settings" } },
    { element: MembersPage, path: ROUTES.members, permission: { action: "read", subject: "members" } },
    { element: SettingsPage, path: ROUTES.settings, permission: { action: "read", subject: "settings" } },
    { element: MemberPage, path: ROUTES.member, permission: { action: "update", subject: "members" } },
    { element: InvoicePage, path: ROUTES.invoice, permission: { action: "update", subject: "invoices" } },
    { element: ServicePage, path: ROUTES.service, permission: { action: "update", subject: "services" } },
    { element: ViewStoragePage, path: ROUTES.storage, permission: { action: "read", subject: "storages" } },
    { element: ClientPage, path: ROUTES.client, permission: { action: "read", subject: "clients" } },
    { element: ClientCreatePage, path: ROUTES.newClient, permission: { action: "create", subject: "clients" } },
    { element: ClientEditPage, path: ROUTES.editClient, permission: { action: "update", subject: "clients" } },
    { element: EditStoragePage, path: ROUTES.editStorage, permission: { action: "update", subject: "storages" } },
    { element: NewStoragePage, path: ROUTES.newStorage, permission: { action: "create", subject: "storages" } },
    { element: DriverLicensesPage, path: ROUTES.driverLicenses, permission: { action: "read", subject: "driver-licenses" } },
    { element: DriverLicensePage, path: ROUTES.driverLicense, permission: { action: "update", subject: "driver-licenses" } },
    { element: CarRentalCostsPage, path: ROUTES.carRentalCosts, permission: { action: "read", subject: "car-rental-costs" } },
    { element: SupportTicketsPage, path: ROUTES.supportTickets, permission: { action: "read", subject: "support-tickets" } },
    { element: ClientRecordsPage, path: ROUTES.clientRecords, permission: { action: "read", subject: "client-records" } },
    { element: ContractRecordsPage, path: ROUTES.contractRecords, permission: { action: "read", subject: "contract-records" } },
    { element: ClientRecordPage, path: ROUTES.clientRecord, permission: { action: "update", subject: "client-records" } },
    {
      element: ClientRecordAssignUserPage,
      path: ROUTES.clientRecordAssignUser,
      permission: { action: "update", subject: "client-records" },
    },
    {
      element: ClientRecordCreateUserPage,
      path: ROUTES.clientRecordCreateUser,
      permission: { action: "update", subject: "client-records" },
    },
    {
      element: ClientRecordCreateContractPage,
      path: ROUTES.clientRecordCreateContract,
      permission: { action: "update", subject: "client-records" },
    },
    {
      element: ClientRecordEditContractPage,
      path: ROUTES.clientRecordEditContract,
      permission: { action: "update", subject: "client-records" },
    },
    { element: SupportTicketTypesPage, path: ROUTES.supportTicketTypes, permission: { action: "read", subject: "support-ticket-types" } },
    { element: SupportTicketTypePage, path: ROUTES.supportTicketType, permission: { action: "update", subject: "support-ticket-types" } },
    { element: ViewSupportTicketPage, path: ROUTES.supportTicket, permission: { action: "read", subject: "support-tickets" } },
    { element: EvChargersPage, path: ROUTES.evChargers, permission: { action: "read", subject: "ev-chargers" } },
    { element: EvChargerPage, path: ROUTES.evCharger, permission: { action: "read", subject: "ev-chargers" } },
    { element: EvTransactionsPage, path: ROUTES.evTransactions, permission: { action: "read", subject: "storages" } },
    { element: HeatPumpsPage, path: ROUTES.heatPumps, permission: { action: "read", subject: "heat-pumps" } },
    { element: HeatPumpPage, path: ROUTES.heatPump, permission: { action: "read", subject: "heat-pumps" } },
  ];
  const ids = useId(pages.length);

  return (
    <SettingsProvider>
      <AuthProivder>
        <GoogleProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Layout />}>
                {pages.map((page, i) => (
                  <Route
                    key={ids[i]}
                    element={
                      <ProtectedRoute permission={page.permission}>
                        <page.element />
                      </ProtectedRoute>
                    }
                    path={page.path}
                  />
                ))}
                {/* Users used to live under /clients — keep old links and bookmarks working. */}
                <Route path="/clients/*" element={<ClientsRedirect />} />
                <Route path="/clients" element={<Navigate to={ROUTES.clients} replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </GoogleProvider>
      </AuthProivder>
    </SettingsProvider>
  );
};

export default Pages;
